import { useEffect, useState, useRef, useMemo } from "react";
import { useOutletContext, useLocation } from "react-router-dom";
import { updateMessageSeen } from "@endpoints/job.service";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import PaperSendIcon from "@assets/svg/PaperSendIcon";
import useChat from "../../../custom-hooks/useChat";
import MessageBubble from "./MessageBubble";
import PaperClipIcon from "@assets/svg/PaperClipIcon";
import NewMessageDivider from "./NewMessageDivider";
import { ISelectedContact } from "../MessagesPage";

interface ChatListProps {
  selectedContact:ISelectedContact
}

const ChatList = ({ selectedContact }: ChatListProps) => {
  const isInitialLoad = useRef(false);
  const {
    isMessageLoading,
    messages,
    sendMessage,
    loadOlderMessages,
    isOlderMessageLoading,
    triggerTyping,
    isTyping,
    hasNextPage
  } = useChat({
    jobId:selectedContact?.jobId,
    receiverDetails: selectedContact?.participantDetails,
  });
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCurrentlyTypingRef = useRef(false);

  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el || isOlderMessageLoading) return;
    const isAtTop = el.scrollTop === 0;
    if (isAtTop && hasNextPage) {
      loadOlderMessages();
    }
  };

  const scrollToBottom = (smooth = false) => {
    if (messagesContainerRef?.current) {
      messagesContainerRef?.current.scrollTo({
        top: messagesContainerRef?.current?.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    } else {
      if (!isCurrentlyTypingRef.current) {
        triggerTyping(true);
        isCurrentlyTypingRef.current = true;
      }

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        triggerTyping(false);
        isCurrentlyTypingRef.current = false;
      }, 2000);
    }
  };

  const handleSendClick = () => {
    if (isLoading || !message.trim() || !selectedContact) return;
    sendMessage({ message, setMessage });

    triggerTyping(false);
    isCurrentlyTypingRef.current = false;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
  };

  //   useEffect(() => {
  //     if (messages?.length > 0) {
  //       // markMessagesAsSeen();
  //       scrollToBottom();
  //     }
  //   }, [messages]);

  useEffect(() => {
    if (!messages?.length) return;

    const el = messagesContainerRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isNearBottom = distanceFromBottom < 150;
    // 20 is fetch message limit
    if (isNearBottom || isInitialLoad?.current) {
      isInitialLoad.current = false;
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (selectedContact?.jobId) isInitialLoad.current = true;
  }, [selectedContact?.jobId]);

  const unseenMessageFrom = useMemo(
    () => messages?.find((item) => !item?.seen),
    [messages]
  );

  const markMessagesAsSeen = async () => {
    if (!messages[0]?.messageData?.length) return;

    const unseenMessageIds = messages[0].messageData
      .filter((msg) => !msg?.seen)
      .map((msg) => msg?._id || msg?.messageId);

    if (unseenMessageIds.length === 0) return;

    try {
      await updateMessageSeen({ messagesIds: unseenMessageIds });
    } catch (error) {
      console.error("Error marking messages as seen:", error);
    }
  };


  // console.log('this is selected contact',selectedContact,isMessageLoading)

  if (!selectedContact || isMessageLoading) {
    return (
      <div className="flex justify-center mt-40">
        <LoadingAnimation color="black" />
      </div>
    );
  }


  return (
    <div className="flex flex-col h-full w-full relative px-5 pt-2 pb-8 min-h-0">
      <div className="bg-[#fafafa] rounded-md flex flex-col flex-1 min-h-0">
        <div
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="flex flex-col gap-5 flex-grow overflow-y-auto no-scrollbar p-6 min-h-0 h-[69vh]"
        >
          {isOlderMessageLoading ? (
            <div className="flex items-center justify-center font-medium text-xs gap-3">
              Loading older messages <LoadingAnimation color="#2e2e2e" />
            </div>
          ) : null}
          {messages?.length ? (
            messages?.map((item) => {
              return (
                <div
                  key={(item?._id || item?.tempMessageId || item?.messageId || item?.chatId)+item?.createdAt}
                  className="flex flex-col gap-2"
                >
                  {/* {item?._id} {unseenMessageFrom?._id} */}
                  {/* {item?._id === unseenMessageFrom?._id ? (
                    <NewMessageDivider />
                  ) : null} */}
                  <MessageBubble item={item} receiverDetails={selectedContact?.participantDetails}/>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-center flex h-[67vh] items-center justify-center pb-10">
              No messages available.
            </p>
          )}
        </div>

        {/* Typing and message Input area */}
        <div className="flex flex-col px-1.5 pb-1.5 pt-0">
          <div className="h-6 flex items-center px-2">
            {isTyping && (
              <span className="text-xs text-gray-500 italic">
                Typing...
              </span>
            )}
          </div>
          <div className="flex gap-4 rounded-md border p-1.5 bg-white">
            <textarea
              className="px-3 py-2 w-full bg-white rounded text-xs font-medium focus:border-gray-300 focus:outline-none"
              rows={1}
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
            ></textarea>
            <button
              className="text-xs text-primary font-medium bg-white px-1 hover:bg-gray-200 flex items-center size-7 justify-center rounded-md disabled:cursor-not-allowed"
              onClick={handleSendClick}
              disabled={isLoading || !message.trim() || !selectedContact}
            >
              {isLoading ? (
                <LoadingAnimation color="black" className={"w-4 h-4"} />
              ) : (
                <PaperSendIcon />
              )}
            </button>
            <button
              className="text-xs text-primary font-medium bg-white px-1 hover:bg-gray-200 flex items-center size-7 justify-center rounded-md"
            // disabled={isLoading || !message.trim() || !selectedContact}
            >
              <PaperClipIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatList;

